import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '@/api/client';
import { AllianceRuleOut, AllianceStatsOut, MeOut, getMe, getStats } from '@/api/alianca';
import { AdesaoModal } from '@/components/alianca/AdesaoModal';
import {
  AliancaHeader,
  ArcStrip,
  Aurora,
  Grain,
  StatBlock
} from '@/components/alianca/AliancaPrimitives';
import { BowArc } from '@/components/ui/BowArc';
import { Button } from '@/components/ui/Button';
import { Eyebrow, MoneyDisplay } from '@/components/ui/Typography';
import {
  NIVEIS,
  proxNivel,
  recargasParaAlcancar,
  tierByKey,
  tierFromInfo
} from '@/lib/alianca-tiers';
import { formatBRL } from '@/lib/format';

/**
 * Aba "Aliança" — Painel sem scroll (cliente ativo) + V4 Captação
 * (cliente sem adesão).
 *
 * Membro: hero vira faixa de identidade (nível + %) e o resto é um
 * dashboard fixo na dobra — arco, card-herói âmbar com o bônus extra +
 * projeção no próximo nível, kWh, recargas e barra de progresso. O
 * cashback ganho deixa de morar abaixo da dobra (motivação do redesign).
 *
 * Fonte do design: Claude Design "Aliança Cashback Sem Scroll.html"
 * (alianca-noscroll.jsx · AliancaNSPainel, Opção C · Painel).
 */
export default function Alianca() {
  const [me, setMe] = useState<MeOut | null>(null);
  const [stats, setStats] = useState<AllianceStatsOut | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);

  async function reload() {
    setLoading(true);
    try {
      const m = await getMe();
      setMe(m);
      if (m.membership && m.membership.ativa) {
        try {
          const s = await getStats();
          setStats(s);
        } catch {
          setStats(null);
        }
      } else {
        setStats(null);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'erro ao carregar');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <BowArc size={48} variant="gradient" spin />
      </div>
    );
  }

  if (!me?.membership || !me.membership.ativa) {
    return (
      <>
        <AliancaCaptacao
          hasTermo={!!me?.termo_vigente}
          onAderir={() => setShowModal(true)}
          rule={me?.rule ?? null}
        />
        {showModal && me?.termo_vigente && (
          <AdesaoModal
            termo={me.termo_vigente}
            onClose={() => setShowModal(false)}
            onSuccess={() => {
              setShowModal(false);
              void reload();
            }}
          />
        )}
        {error && (
          <p className="px-4 pb-6 text-center text-sm text-bow-red">{error}</p>
        )}
      </>
    );
  }

  return <AliancaPainel me={me} stats={stats} />;
}

/* ============================================================
   OPÇÃO C — PAINEL · sem scroll
   Faixa de identidade em cima, dashboard fixo embaixo — tudo na dobra.
   ============================================================ */

interface PainelProps {
  me: MeOut;
  stats: AllianceStatsOut | null;
}

/** Card branco do painel (linhas 2 e 3 da grade). */
function PanelCard({ children }: { children: ReactNode }) {
  return (
    <div
      className="rounded-[18px] bg-white p-3.5"
      style={{
        boxShadow: '0 1px 2px rgba(0,27,116,0.04), 0 4px 14px rgba(0,27,116,0.06)'
      }}
    >
      {children}
    </div>
  );
}

export function AliancaPainel({ me, stats }: PainelProps) {
  const [showInfo, setShowInfo] = useState(false);
  const m = me.membership!;
  const cur = tierByKey(m.nivel);
  // % de cashback efetivo do nível atual vem do backend (admin), não do
  // estático — `tierByKey` só fornece cor/label/versículo.
  const curPct = m.bonus_alianca_pct;
  const kwhAcum = Number(m.kwh_acumulados_90d);
  // Backend é fonte de verdade: próximo nível + kWh faltantes vêm da regra
  // configurada no admin (`me.proximo_nivel` / `kwh_faltam_proximo_nivel`),
  // com fallback no estático se a API não trouxer.
  const nxt = tierFromInfo(me.proximo_nivel) ?? proxNivel(kwhAcum) ?? null;
  const kwhFaltam =
    me.kwh_faltam_proximo_nivel != null
      ? Math.max(0, Number(me.kwh_faltam_proximo_nivel))
      : nxt
        ? Math.max(0, nxt.min - kwhAcum)
        : 0;
  // Média real de kWh por recarga do próprio cliente (desde adesão); cai no
  // default 35 se ainda não houver histórico.
  const kwhMedio =
    stats && stats.total_recargas_desde_adesao > 0
      ? Number(stats.total_kwh_desde_adesao) / stats.total_recargas_desde_adesao
      : 35;
  const recargasFaltam = nxt ? recargasParaAlcancar(kwhFaltam, kwhMedio) : 0;

  // "No próximo nível esse mesmo padrão renderia R$ X"
  // Usa o cashback acumulado desde adesão como proxy de "padrão atual"
  // (não temos janela 90d isolada — só desde adesão).
  const cashbackAtual = stats ? Number(stats.bonus_extra_acumulado_brl) : 0;
  const cashbackNoProx =
    nxt && curPct > 0 ? cashbackAtual * (nxt.pct / curPct) : 0;
  const showProjecao =
    nxt != null && curPct > 0 && cashbackNoProx - cashbackAtual > 0.005;

  // Barra "Rumo ao próximo": posição dentro da faixa atual (cur.min → nxt.min).
  // cur.min vem do estático — se a regra do admin divergir, o clamp segura.
  const span = nxt ? nxt.min - cur.min : 0;
  const progressPct =
    nxt && span > 0
      ? Math.min(100, Math.max(0, Math.round(((kwhAcum - cur.min) / span) * 100)))
      : 100;

  return (
    // Sem scroll = garantia dura: viewport fixa (100dvh − BottomNav) +
    // overflow-hidden, mesmo shell da AliancaCaptacao. Negative margins
    // neutralizam o px-4/pb-24/padding-top do AppLayout.
    <div
      className="-mx-4 -mb-24 flex flex-col overflow-hidden"
      style={{
        marginTop: 'calc(-1 * max(1rem, env(safe-area-inset-top)))',
        height:
          'calc(100dvh - 62px - max(0.75rem, env(safe-area-inset-bottom)))',
        background: '#F1EDE4'
      }}
    >
      {/* Faixa de identidade — hero compacto: nível + % lado a lado */}
      <section
        className="relative flex-none overflow-hidden text-white"
        style={{
          paddingTop: 'max(1rem, env(safe-area-inset-top))',
          paddingBottom: 22
        }}
      >
        <Aurora from={cur.hex} to={nxt ? nxt.hex : cur.hex} />
        <Grain opacity={0.1} />
        {/* BowArc decorativo girando atrás do conteúdo */}
        <div
          className="absolute"
          style={{ top: -110, right: -110, opacity: 0.22, pointerEvents: 'none' }}
        >
          <BowArc size={320} variant="white" spin />
        </div>

        <div className="relative px-5">
          <AliancaHeader dark onInfo={() => setShowInfo(true)} />

          <div
            className="flex items-end justify-between"
            style={{ padding: '6px 4px 0' }}
          >
            <div>
              <Eyebrow color="rgba(255,255,255,0.75)">Seu nível</Eyebrow>
              <div
                style={{
                  fontFamily: 'Chillax, system-ui, sans-serif',
                  fontWeight: 500,
                  fontSize: 42,
                  lineHeight: 0.95,
                  letterSpacing: '-0.035em',
                  marginTop: 4
                }}
              >
                {cur.label}
              </div>
            </div>
            <span
              className="inline-flex items-baseline rounded-full"
              style={{
                gap: 5,
                padding: '7px 13px',
                marginBottom: 3,
                background: 'rgba(255,255,255,0.18)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)'
              }}
            >
              <span style={{ fontSize: 20, fontWeight: 600, letterSpacing: '-0.02em' }}>
                {curPct}%
              </span>
              <span className="text-[11px]" style={{ opacity: 0.85 }}>
                de cashback
              </span>
            </span>
          </div>
        </div>
      </section>

      {/* Painel — fundo cream. `relative` é essencial: sem ele o hero
          (positioned) pinta por cima da sobreposição de -mt-4 e esconde os
          cantos arredondados da sheet. */}
      <section
        className="relative -mt-4 min-h-0 flex-1 rounded-t-[28px] px-5 pb-2 pt-4"
        style={{ background: '#F1EDE4' }}
      >
        <ArcStrip kwh={kwhAcum} />

        <div className="mt-3.5 flex flex-col gap-2.5">
          {/* Card-herói âmbar: bônus extra + projeção no próximo nível */}
          <div
            className="flex items-center gap-3.5 rounded-[18px] p-4"
            style={{
              background: 'linear-gradient(135deg,#FFB92A 0%,#DE7C00 100%)',
              color: '#001B74'
            }}
          >
            <div className="min-w-0 flex-1">
              <Eyebrow color="rgba(0,27,116,0.6)">Bônus extra · Aliança</Eyebrow>
              <div className="mt-1">
                {stats ? (
                  <MoneyDisplay value={cashbackAtual} size={32} weight={700} />
                ) : (
                  <span
                    style={{
                      fontFamily: 'Chillax, system-ui, sans-serif',
                      fontWeight: 700,
                      fontSize: 32,
                      lineHeight: 1,
                      letterSpacing: '-0.025em'
                    }}
                  >
                    —
                  </span>
                )}
              </div>
              <div
                className="mt-1 text-[11.5px]"
                style={{ color: 'rgba(0,27,116,0.8)' }}
              >
                a mais que a Escala daria sozinha · desde adesão
              </div>
            </div>
            {showProjecao && nxt && (
              <div className="flex-none text-right">
                <div
                  className="text-[10px] font-semibold uppercase tracking-[0.06em]"
                  style={{ color: 'rgba(0,27,116,0.6)' }}
                >
                  no {nxt.label}
                </div>
                <div
                  style={{
                    fontFamily: 'Chillax, system-ui, sans-serif',
                    fontWeight: 600,
                    fontSize: 19,
                    letterSpacing: '-0.02em',
                    marginTop: 2
                  }}
                >
                  {formatBRL(cashbackNoProx)}
                </div>
              </div>
            )}
          </div>

          {/* Linha 2 — kWh + recargas */}
          <div className="grid grid-cols-2 gap-2.5">
            <PanelCard>
              <StatBlock
                label="Acumulado"
                value={Math.round(kwhAcum).toLocaleString('pt-BR')}
                hint="kWh / 90 dias"
              />
            </PanelCard>
            <PanelCard>
              <StatBlock
                label="Recargas"
                value={stats ? stats.total_recargas_desde_adesao : '—'}
                hint="desde adesão"
              />
            </PanelCard>
          </div>

          {/* Linha 3 — progresso pro próximo nível (ou topo do arco) */}
          {nxt ? (
            <PanelCard>
              <div className="flex items-center justify-between">
                <span
                  className="text-[11px] font-semibold uppercase tracking-[0.06em]"
                  style={{ color: 'rgba(0,27,116,0.5)' }}
                >
                  Rumo ao {nxt.label}
                </span>
                <span
                  className="rounded-full text-[10.5px] font-bold text-white"
                  style={{ padding: '3px 9px', background: nxt.hex }}
                >
                  +{nxt.pct - curPct}%
                </span>
              </div>
              <div
                className="relative rounded-full"
                style={{
                  height: 10,
                  background: 'rgba(0,27,116,0.08)',
                  margin: '10px 0 8px'
                }}
              >
                <div
                  className="absolute left-0 top-0 h-full rounded-full"
                  style={{
                    width: `${progressPct}%`,
                    background: `linear-gradient(90deg, ${cur.hex}, ${nxt.hex})`
                  }}
                />
              </div>
              <div className="text-[12px]" style={{ color: 'rgba(0,27,116,0.65)' }}>
                Faltam{' '}
                <strong style={{ color: '#001B74' }}>
                  {kwhFaltam.toFixed(0)} kWh
                </strong>{' '}
                · ≈ {recargasFaltam}{' '}
                {recargasFaltam === 1 ? 'recarga' : 'recargas'} no seu ritmo
              </div>
            </PanelCard>
          ) : (
            <PanelCard>
              <span
                className="text-[11px] font-semibold uppercase tracking-[0.06em]"
                style={{ color: 'rgba(0,27,116,0.5)' }}
              >
                Topo do arco
              </span>
              <div
                className="mt-1.5"
                style={{
                  fontFamily: 'Chillax, system-ui, sans-serif',
                  fontWeight: 500,
                  fontSize: 20,
                  letterSpacing: '-0.02em',
                  color: '#001B74'
                }}
              >
                Você chegou no Violeta — plenitude.
              </div>
            </PanelCard>
          )}
        </div>
      </section>

      {showInfo && <AliancaInfoDrawer onClose={() => setShowInfo(false)} />}
    </div>
  );
}

/* ============================================================
   E — CAPTAÇÃO · Colunas equalizer navy (cliente sem adesão)
   Fonte: bundle Claude Design alianca-captacao-min.jsx (CaptacaoColunas).
   Tela única sem scroll. Fundo navy. 7 colunas ascendentes na cor de
   cada nível, altura ∝ % configurado no admin.
   ============================================================ */

interface CaptacaoProps {
  hasTermo: boolean;
  onAderir: () => void;
  rule: AllianceRuleOut | null;
}

/** Mescla cores/labels estáticos com os % reais da regra do admin. */
function resolvedTiers(rule: AllianceRuleOut | null) {
  return NIVEIS.map((n) => ({
    ...n,
    pct: rule?.tiers.find((t) => t.name === n.key)?.percent ?? n.pct,
  }));
}

function AliancaCaptacao({ hasTermo, onAderir, rule }: CaptacaoProps) {
  const navigate = useNavigate();
  const tiers = resolvedTiers(rule);
  const maxPct = Math.max(...tiers.map((t) => t.pct), 1);
  // NSS (nss-amenities.jsx · AliancaCaptacaoNS): as 7 barras são a banda
  // flexível — 220 → 150 em telas baixas, a régua inteira sempre aparece.
  const MAX_BAR_H =
    typeof window !== 'undefined' && window.innerHeight <= 740 ? 150 : 220;

  return (
    <div
      className="-mx-4 -mb-24 flex flex-col overflow-hidden bg-bow-royal"
      style={{
        marginTop: 'calc(-1 * max(1rem, env(safe-area-inset-top)))',
        height:
          'calc(100dvh - 62px - max(0.75rem, env(safe-area-inset-bottom)))',
        paddingTop: 'max(0.5rem, env(safe-area-inset-top))',
      }}
    >
      {/* Barra de topo — label + botão X */}
      <div className="flex items-center justify-between px-5 pt-2 pb-0">
        <span
          className="text-[12px] font-semibold uppercase tracking-[0.1em]"
          style={{ color: 'rgba(241,237,228,0.7)' }}
        >
          A Aliança
        </span>
        <button
          type="button"
          aria-label="Fechar"
          onClick={() => navigate(-1)}
          className="inline-flex h-[34px] w-[34px] items-center justify-center rounded-full border-0"
          style={{ background: 'rgba(241,237,228,0.14)', color: '#F1EDE4' }}
        >
          <XIcon />
        </button>
      </div>

      {/* Hero textual */}
      <div className="px-6 pt-3 text-center">
        <div
          className="text-[12px] font-semibold uppercase tracking-[0.16em]"
          style={{ color: 'rgba(241,237,228,0.7)' }}
        >
          até
        </div>
        <div
          className="[@media(max-height:740px)]:!text-[52px]"
          style={{
            fontFamily: 'Chillax, system-ui, sans-serif',
            fontWeight: 600,
            fontSize: 64,
            letterSpacing: '-0.035em',
            lineHeight: 0.95,
            marginTop: 2,
            background: 'linear-gradient(120deg,#FFB92A 0%,#00C072 42%,#0090E2 68%,#8345BA 100%)',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            color: 'transparent',
          }}
        >
          {maxPct}% de volta
        </div>
      </div>

      {/* Colunas ascendentes */}
      <div
        className="flex flex-1 items-end justify-center gap-2 px-[22px] pt-5 pb-2"
      >
        {tiers.map((t) => {
          const barH = Math.max(10, (t.pct / maxPct) * MAX_BAR_H);
          return (
            <div
              key={t.key}
              className="flex flex-1 flex-col items-center gap-2"
            >
              <span
                style={{
                  fontFamily: 'Chillax, system-ui, sans-serif',
                  fontWeight: 600,
                  fontSize: 15,
                  letterSpacing: '-0.02em',
                  color: '#F1EDE4',
                  lineHeight: 1,
                }}
              >
                {t.pct}%
              </span>
              <div
                style={{
                  width: '100%',
                  height: barH,
                  borderRadius: '8px 8px 4px 4px',
                  background: t.hex,
                  boxShadow: `0 0 18px ${t.hex}66`,
                }}
              />
              <span
                style={{
                  fontSize: 9.5,
                  fontWeight: 600,
                  letterSpacing: '0.02em',
                  color: 'rgba(241,237,228,0.6)',
                  textTransform: 'uppercase',
                }}
              >
                {t.label.slice(0, 3)}
              </span>
            </div>
          );
        })}
      </div>

      {/* CTA */}
      <div className="px-6 pt-2 pb-6">
        {hasTermo ? (
          <button
            type="button"
            onClick={onAderir}
            className="inline-flex h-14 w-full items-center justify-center gap-2 rounded-full border-0 text-[16px] font-semibold text-bow-royal"
            style={{ background: '#F1EDE4', letterSpacing: '-0.01em', cursor: 'pointer' }}
          >
            Aderir grátis
            <ArrowIcon />
          </button>
        ) : (
          <div
            className="rounded-2xl px-4 py-3 text-center text-sm"
            style={{ background: 'rgba(241,237,228,0.12)', color: 'rgba(241,237,228,0.7)' }}
          >
            O termo do programa ainda não foi publicado. Volte em breve.
          </div>
        )}
        <div
          className="mt-[11px] text-center text-[12px] font-medium"
          style={{ color: 'rgba(241,237,228,0.6)' }}
        >
          Grátis · sem mensalidade · cancelável quando quiser
        </div>
      </div>
    </div>
  );
}

function XIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      aria-hidden
    >
      <path d="M6 6l12 12M18 6 6 18" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg
      width="19"
      height="19"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  );
}

/* ============================================================
   Drawer informativo — acionado pelo "i" do header (tela do membro).
   ============================================================ */

function AliancaInfoDrawer({ onClose }: { onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-bow-royal/40 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="flex max-h-[88vh] w-full max-w-md flex-col rounded-t-3xl bg-bow-cream sm:mb-8 sm:rounded-3xl">
        <div className="border-b border-bow-royal/10 px-6 py-4">
          <Eyebrow>Como funciona</Eyebrow>
          <p className="mt-1 text-lg font-semibold text-bow-royal">
            O programa Aliança
          </p>
        </div>
        <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5 text-sm leading-relaxed text-bow-royal/80">
          <p>
            A Aliança é o programa de fidelidade da Bow. Quanto mais você carrega,
            mais cashback recebe ao adicionar saldo — sem mensalidade e sem letra
            miúda.
          </p>
          <div>
            <span className="font-semibold text-bow-royal">7 níveis, 7 cores.</span>{' '}
            Do Vermelho ao Violeta, cada faixa libera um percentual maior de
            cashback (até 30%). O percentual de cada nível é definido pela Bow e
            pode mudar ao longo do tempo.
          </div>
          <div>
            <span className="font-semibold text-bow-royal">Janela de 90 dias.</span>{' '}
            Seu nível é calculado pelos kWh carregados nos últimos 90 dias. É uma
            janela móvel: continue carregando para subir e se manter no topo.
          </div>
          <div>
            <span className="font-semibold text-bow-royal">
              Cashback ao adicionar saldo.
            </span>{' '}
            O bônus do seu nível é aplicado a cada PIX que você faz para recarregar
            a carteira. O cashback vale em tudo na Bow — recarga, aspirador e os
            demais serviços.
          </div>
          <p className="text-bow-royal/60">
            Aderir é grátis e você pode cancelar a qualquer momento.
          </p>
        </div>
        <div
          className="border-t border-bow-royal/10 bg-white px-6 pt-4"
          style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
        >
          <Button variant="primary" size="md" fullWidth onClick={onClose}>
            Entendi
          </Button>
        </div>
      </div>
    </div>
  );
}
